import { Theme } from "@mui/material/styles";
import { differenceInSeconds } from "date-fns/differenceInSeconds";
import { format } from "date-fns/format";
import i18n from "../../services/localization-service/i18n";
import { IObjectHistoryDataQualityEventItem, IObjectHistoryDiagnosticEventItem, IObjectHistoryFormEventItem, IObjectHistoryWaterLossEventItem, ObjectHistoryItemTypeEnum } from "../../queries/object-history/Common";
import { isEqual } from "lodash";
import { FormFeatureEnum } from "../../views/object-form-view/Models";
import { WaterLossCauseEnum } from "../../common/enums/WaterLossCauseEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";

export function getEventColor(theme: Theme, item: IObjectHistoryDataQualityEventItem | IObjectHistoryDiagnosticEventItem | IObjectHistoryWaterLossEventItem): string | undefined {
    return item.isStart
        ? theme.palette.error.main
        : theme.palette.text.primary;
}

export function getEventDurationText(item: IObjectHistoryDataQualityEventItem | IObjectHistoryDiagnosticEventItem | IObjectHistoryWaterLossEventItem): string {
    const seconds = differenceInSeconds(item.endTime ?? 0, item.startTime ?? 0);
    return seconds < 60.0
        ? `${seconds.toFixed(2)} ${i18n.t("datetime.seconds")}`
        : seconds < 3600.0
            ? `${(seconds / 60).toFixed(2)} ${i18n.t("datetime.minutes")}`
            : `${(seconds / 3600).toFixed(2)} ${i18n.t("datetime.hours")}`;
}

export function getEventStartSynopsisText(item: IObjectHistoryDataQualityEventItem | IObjectHistoryDiagnosticEventItem | IObjectHistoryFormEventItem | IObjectHistoryWaterLossEventItem): string {
    switch (item.type) {
        case ObjectHistoryItemTypeEnum.DataQualityEvent:
        case ObjectHistoryItemTypeEnum.DiagnosticEvent:

            return item.objectType === eLynxObjectTypeEnum.Well
                ? `${i18n.t("objectHistory.eventItem.wellAbnormalSynopsis")} ${i18n.t("objectHistory.eventItem.wellDiagnosis")}`
                : `${i18n.t("objectHistory.eventItem.deviceAbnormalSynopsis")} ${i18n.t("objectHistory.eventItem.deviceDiagnosis")}`;

        case ObjectHistoryItemTypeEnum.FormEvent:

            return (((item as IObjectHistoryFormEventItem).formFeatures & FormFeatureEnum.allowClose) !== FormFeatureEnum.allowClose) ||
                (item.endTime && isEqual(item.endTime, item.startTime))
                ? `${i18n.t("objectHistory.eventItem.formCreated", item)}`
                : `${i18n.t("objectHistory.eventItem.formOpened", item)}`;

        case ObjectHistoryItemTypeEnum.WaterLossEvent:
            return `${i18n.t("objectHistory.eventItem.waterLossAbnormalSynopsis")}`;
        default:
            return "";
    }
}

export function getEventEndSynopsisText(item: IObjectHistoryDataQualityEventItem | IObjectHistoryDiagnosticEventItem | IObjectHistoryFormEventItem | IObjectHistoryWaterLossEventItem): string {
    switch (item.type) {
        case ObjectHistoryItemTypeEnum.DataQualityEvent:
        case ObjectHistoryItemTypeEnum.DiagnosticEvent:

            return item.objectType === eLynxObjectTypeEnum.Well
                ? i18n.t("objectHistory.eventItem.wellNormalSynopsis")
                : i18n.t("objectHistory.eventItem.deviceNormalSynopsis");

        case ObjectHistoryItemTypeEnum.FormEvent:

            return `${i18n.t("objectHistory.eventItem.formClosed", item)}`;

        case ObjectHistoryItemTypeEnum.WaterLossEvent:
            return `${i18n.t("objectHistory.eventItem.waterLossNormalSynopsis")}`;

        default:
            return "";
    }
}

export function getEventEndSummaryText(item: IObjectHistoryDataQualityEventItem | IObjectHistoryDiagnosticEventItem | IObjectHistoryWaterLossEventItem): string {

    switch (item.type) {
        case ObjectHistoryItemTypeEnum.DataQualityEvent:
        case ObjectHistoryItemTypeEnum.DiagnosticEvent:
            return i18n.t("objectHistory.eventItem.endSummary", { startTime: format(item.startTime, "P pp"), durationText: getEventDurationText(item) });

        case ObjectHistoryItemTypeEnum.WaterLossEvent:
            const waterLossItem = item as IObjectHistoryWaterLossEventItem;
            return i18n.t("objectHistory.eventItem.waterLossEndSummary", {
                startTime: format(item.startTime, "P pp"),
                durationText: getEventDurationText(item),
                estimatedLoss: waterLossItem.estimatedLoss ?? "unknown"
            });

        default:
            return "";
    }
}

export function getWaterLossCauseText(item: IObjectHistoryWaterLossEventItem) {
    switch (item.cause) {
        case WaterLossCauseEnum.LineBreak3rdParty:
            return i18n.t("enums.waterLossCause.lineBreak3rdParty").toLowerCase();

        case WaterLossCauseEnum.LineBreakNon3rdParty:
            return i18n.t("enums.waterLossCause.lineBreakNon3rdParty").toLowerCase();

        case WaterLossCauseEnum.UnauthorizedUsage:
            return i18n.t("enums.waterLossCause.unauthorizedUsage").toLowerCase();

        case WaterLossCauseEnum.FireDepartment:
            return i18n.t("enums.waterLossCause.fireDepartment").toLowerCase();

        case WaterLossCauseEnum.SystemMaintenance:
            return i18n.t("enums.waterLossCause.systemMaintenance").toLowerCase();

        case WaterLossCauseEnum.Other:
            return i18n.t("enums.waterLossCause.other").toLowerCase();

        default:
            return "";
    }
}
